import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import GaDashboardCanvas from './GaDashboardCanvas';
import { getCachedGaDrawingPage, prefetchGaDrawingPage, warmSequencePages } from './gaDrawingCache';
import { captureGaStage, waitFor } from './gaPdfExport';
import { PDF_ZOOM_SETTLE_MS } from './renderQuality';

/**
 * Vertical stack of extracted PDF pages. Nearby pages paint first; the rest
 * warm into the day-cache so a later open does not re-download.
 */
const GaSequenceScroller = forwardRef(({
  dashboardId,
  drawingVersion,
  pageCount,
  pageTitles = [],
  fetchBlob,
  markers,
  resolvedById,
  compareById,
  viewDate,
  editMode,
  tool,
  selectedMarkerId,
  onSelectMarker,
  onCreateGeometry,
  onMoveMarker,
  onMoveLabel,
  labelMode,
  renderQualityId,
  balloonScale = 1,
  activePage,
  onActivePage,
  exportRef,
}, ref) => {
  const rootRef = useRef(null);
  const slotRefs = useRef({});
  const [visible, setVisible] = useState(() => new Set([1, 2]));
  const [urls, setUrls] = useState({});
  const [forcePage, setForcePage] = useState(null);

  const pages = useMemo(
    () => Array.from({ length: Math.max(1, pageCount) }, (_, i) => i + 1),
    [pageCount]
  );

  // Only the active / exporting slide renders at the personal quality; the rest
  // stay cheap. Export compares this exact id against what a slide has painted.
  const fullQualityId = renderQualityId || 'balanced';

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        setVisible((prev) => {
          const next = new Set(prev);
          let best = { page: activePage, ratio: 0 };
          for (const entry of entries) {
            const page = parseInt(entry.target.dataset.gaPage, 10);
            if (!page) continue;
            if (entry.isIntersecting) next.add(page);
            if (entry.intersectionRatio > best.ratio) {
              best = { page, ratio: entry.intersectionRatio };
            }
          }
          if (best.ratio > 0.35 && best.page && best.page !== activePage) {
            onActivePage?.(best.page);
          }
          return next;
        });
      },
      { root, rootMargin: '280px 0px', threshold: [0.15, 0.4, 0.7] }
    );
    for (const n of pages) {
      const el = slotRefs.current[n];
      if (el) io.observe(el);
    }
    return () => io.disconnect();
  }, [pages, activePage, onActivePage]);

  useEffect(() => {
    const want = new Set(visible);
    want.add(activePage || 1);
    if (activePage) want.add(Math.min(pageCount, activePage + 1));
    if (forcePage) want.add(forcePage);
    let cancelled = false;
    [...want].forEach((n) => {
      const cached = getCachedGaDrawingPage(dashboardId, n);
      if (cached?.url) {
        setUrls((prev) => (prev[n] ? prev : { ...prev, [n]: cached.url }));
        return;
      }
      prefetchGaDrawingPage(fetchBlob, dashboardId, n, { version: drawingVersion })
        .then((entry) => {
          if (!cancelled && entry?.url) {
            setUrls((prev) => ({ ...prev, [n]: entry.url }));
          }
        })
        .catch((e) => console.warn(`GA page ${n} failed:`, e.message));
    });
    return () => { cancelled = true; };
  }, [visible, activePage, forcePage, dashboardId, drawingVersion, fetchBlob, pageCount]);

  useEffect(() => {
    if (!dashboardId || pageCount < 2) return undefined;
    const t = setTimeout(() => {
      warmSequencePages(fetchBlob, dashboardId, pageCount, { version: drawingVersion });
    }, 400);
    return () => clearTimeout(t);
  }, [dashboardId, drawingVersion, fetchBlob, pageCount]);

  const jumpTo = (n) => {
    onActivePage?.(n);
    slotRefs.current[n]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useImperativeHandle(ref, () => ({
    async capturePages(pageNumbers, { onProgress } = {}) {
      const stageOf = (n) => slotRefs.current[n]?.querySelector('[data-ga-stage]');
      // Forcing a slide re-rasters it at the export quality, and the previous
      // raster stays on screen meanwhile — so wait for BOTH the quality the
      // slide is now rendering at and its own ready flag before capturing.
      const settled = (n) => {
        const stage = stageOf(n);
        return !!(stage
          && stage.getAttribute('data-ga-quality') === fullQualityId
          && stage.getAttribute('data-ga-ready') === '1');
      };
      const out = [];
      for (const n of pageNumbers) {
        onProgress?.(n);
        setForcePage(n);
        onActivePage?.(n);
        try {
          const cached = getCachedGaDrawingPage(dashboardId, n);
          if (cached?.url) {
            setUrls((prev) => (prev[n] ? prev : { ...prev, [n]: cached.url }));
          } else {
            const entry = await prefetchGaDrawingPage(fetchBlob, dashboardId, n, { version: drawingVersion });
            if (entry?.url) setUrls((prev) => ({ ...prev, [n]: entry.url }));
          }
        } catch (e) {
          console.warn(`GA export page ${n} prefetch failed:`, e.message);
        }
        slotRefs.current[n]?.scrollIntoView({ behavior: 'auto', block: 'start' });
        try {
          await waitFor(() => settled(n));
          // First paint is the cheap preview layer; the sharp one starts right
          // after. Give it the settle window so slides export at full density.
          await new Promise((resolve) => setTimeout(resolve, PDF_ZOOM_SETTLE_MS + 80));
          await waitFor(() => settled(n));
          await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
          const stage = stageOf(n);
          if (!stage) throw new Error('Slide is not on the page');
          const canvas = await captureGaStage(stage, html2canvas, { scale: 2 });
          out.push({ page: n, canvas });
        } catch (e) {
          console.error(`GA export page ${n} failed:`, e);
          out.push({ page: n, canvas: null, error: e.message || String(e) });
        }
      }
      setForcePage(null);
      return out;
    },
  }), [dashboardId, drawingVersion, fetchBlob, onActivePage, fullQualityId]);

  const markerPage = (m) => Math.max(1, parseInt(m.style?.page, 10) || 1);

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', background: '#f1f5f9' }}>
      <div style={{
        width: 196, flexShrink: 0, overflowY: 'auto', background: '#fff',
        borderRight: '1px solid #e2e8f0', padding: '8px 6px',
      }}>
        {pages.map((n) => {
          const title = pageTitles[n - 1] || `Page ${n}`;
          const on = n === activePage;
          const bound = markers.some((m) => markerPage(m) === n && (m.binding?.mode === 'activities' || m.binding?.mode === 'wbs'));
          return (
            <button
              key={n}
              type="button"
              onClick={() => jumpTo(n)}
              title={title}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '7px 8px', marginBottom: 4, borderRadius: 8,
                border: on ? '1px solid #0f766e' : '1px solid transparent',
                background: on ? '#f0fdfa' : 'transparent',
                cursor: 'pointer', fontSize: 11, color: '#334155',
              }}
            >
              <div style={{ fontWeight: 700, color: on ? '#0f766e' : '#0f172a' }}>
                {n}. {bound ? '●' : '○'} {title}
              </div>
            </button>
          );
        })}
      </div>
      <div ref={rootRef} style={{ flex: 1, overflowY: 'auto', padding: '0 0 24px' }}>
        {pages.map((n) => {
          const title = pageTitles[n - 1] || `Page ${n}`;
          const pageMarkers = markers.filter((m) => markerPage(m) === n);
          const ready = !!urls[n];
          const near = visible.has(n) || n === activePage || n === forcePage;
          // Balloons sit on the drawing in the stack — 300px side gutters
          // made every slide 600px wider than the PPT and letterboxed it.
          const stackLabelMode = labelMode === 'off' ? 'off' : 'auto';
          return (
            <div
              key={n}
              data-ga-page={n}
              ref={(el) => { slotRefs.current[n] = el; }}
              style={{
                marginBottom: 2,
                border: n === activePage ? '2px solid #0f766e' : '1px solid #e2e8f0',
                background: '#fff',
                overflow: 'hidden',
              }}
              onClick={() => onActivePage?.(n)}
            >
              <div style={{
                padding: '4px 10px', fontSize: 12, fontWeight: 700, color: '#0f766e',
                background: '#f0fdfa', borderBottom: '1px solid #ccfbf1',
                display: 'flex', justifyContent: 'space-between', gap: 8,
              }}>
                <span>Page {n} of {pageCount} — {title}</span>
                <span style={{ fontWeight: 500, color: '#64748b' }}>
                  {pageMarkers.length} pin{pageMarkers.length === 1 ? '' : 's'}
                </span>
              </div>
              {near && ready ? (
                <div style={{ width: '100%', aspectRatio: '16 / 9', display: 'flex' }}>
                  <GaDashboardCanvas
                    drawingUrl={urls[n]}
                    previewUrl={null}
                    drawingMime="application/pdf"
                    drawingPage={1}
                    markers={pageMarkers}
                    resolvedById={resolvedById}
                    compareById={compareById}
                    viewDate={viewDate}
                    editMode={editMode && n === activePage}
                    tool={n === activePage ? tool : 'select'}
                    selectedMarkerId={selectedMarkerId}
                    onSelectMarker={onSelectMarker}
                    onCreateGeometry={(geometry, kindHint) => onCreateGeometry(geometry, kindHint, n)}
                    onMoveMarker={onMoveMarker}
                    onMoveLabel={onMoveLabel}
                    labelMode={stackLabelMode}
                    balloonScale={balloonScale}
                    gutterPx={0}
                    fitPad={4}
                    renderQualityId={(n === activePage || n === forcePage) ? fullQualityId : 'fast'}
                    exportRef={n === activePage ? exportRef : undefined}
                    allowWheelZoom={false}
                  />
                </div>
              ) : (
                <div style={{
                  width: '100%', aspectRatio: '16 / 9',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#94a3b8', fontSize: 13, background: '#f8fafc',
                }}>
                  {near ? 'Loading this page…' : 'Scroll to load'}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

GaSequenceScroller.displayName = 'GaSequenceScroller';

export default GaSequenceScroller;
